import { forwardRef, useCallback, useMemo, useState } from 'react';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { useParams } from 'react-router-dom';
import type { RouteParams } from '@user/pages/RouteParams';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faPen,
  faCaretDown,
  faCaretRight,
  faFolderOpen,
  faFolderTree,
} from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import type { Workspace } from '@workspace/domain/workspace';
import { SidebarMenuItem } from './SidebarMenuItem';
import { SidebarMenuButton } from './SidebarMenuButton';
import { useCurrentUserId } from '@framework/auth';
import { useBookmark } from '@user/pages/ViewModelPage/hooks';
import { ViewModelDeleteConfirmModal, ViewModelSettingModal } from '@view-model/ui/components/ViewModel';
import { useViewModelCopy } from '@workspace/view-model-folder/hooks';
import { MoveViewModelModal } from '@workspace/view-model-folder/ui/MoveModal/MoveViewModelModal';
import { MoveToOtherWorkspaceModal } from '@workspace/view-model-folder/ui/MoveToOtherWorkspaceModal';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';
import { ViewModelItemTitle } from './ViewModelItemTitle';

type Modals = 'ViewModelEdit' | 'ViewModelDelete' | 'MoveViewModel' | 'MoveToOtherWorkspace';

const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewModelEditModalOpen: useCallback(() => {
      setOpenModal('ViewModelEdit');
    }, []),
    handleViewModelDeleteModalOpen: useCallback(() => {
      setOpenModal('ViewModelDelete');
    }, []),
    handleMoveViewModelModalOpen: useCallback(() => {
      setOpenModal('MoveViewModel');
    }, []),
    handleMoveToOtherWorkspaceModalOpen: useCallback(() => {
      setOpenModal('MoveToOtherWorkspace');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  viewModel: ViewModelEntity;
  workspace: Workspace;
  searchKeyword?: string;
  isOpen?: boolean;
  toggleOpen?: () => void;
  children?: React.ReactNode;
};

export const ViewModelItem = forwardRef<HTMLDivElement, Props>(
  ({ viewModel, workspace, searchKeyword, isOpen, toggleOpen, children }, ref) => {
    const currentUserId = useCurrentUserId();
    const { viewModelId: viewModelIdParam } = useParams<RouteParams>();
    const isSelected = useMemo(() => viewModel.id === viewModelIdParam, [viewModel.id, viewModelIdParam]);
    const { isBookmarked, toggleBookmark } = useBookmark(viewModel.id);

    const {
      openModal,
      handleViewModelEditModalOpen,
      handleViewModelDeleteModalOpen,
      handleMoveViewModelModalOpen,
      handleMoveToOtherWorkspaceModalOpen,
      handleModalClose,
    } = useHandleModals();
    const { isMenuButtonShowable, isEditMenuButtonShowable } = useSidebarItemMenuPermissions(workspace);

    const handleViewModelCopy = useViewModelCopy(viewModel);

    return (
      <div className="relative" ref={ref}>
        <div className="flex items-center">
          {toggleOpen && (
            <button className="mr-1 w-4 focus:outline-none" onClick={toggleOpen}>
              <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
            </button>
          )}
          <SidebarMenuItem
            isSelected={isSelected}
            isMenuButtonShowable={isMenuButtonShowable}
            popupMenu={
              <>
                <SidebarMenuButton
                  handleClick={toggleBookmark}
                  icon={faStar}
                  title={isBookmarked ? 'ブックマークを削除' : 'ブックマーク'}
                  iconClassName={isBookmarked ? 'text-yellow-400' : 'text-gray-400'}
                />
                <SidebarMenuButton handleClick={handleViewModelCopy} icon={faCopy} title={'ビューモデルをコピー'} />
                {isEditMenuButtonShowable && (
                  <>
                    <SidebarMenuButton
                      handleClick={handleMoveViewModelModalOpen}
                      icon={faFolderOpen}
                      title={'ビューモデルを移動'}
                    />
                    <SidebarMenuButton
                      handleClick={handleMoveToOtherWorkspaceModalOpen}
                      icon={faFolderTree}
                      title={'別のワークスペースに移動'}
                    />
                    <SidebarMenuButton handleClick={handleViewModelEditModalOpen} icon={faPen} title={'編集'} />
                    <SidebarMenuButton
                      handleClick={handleViewModelDeleteModalOpen}
                      icon={faTrashAlt}
                      title={'削除'}
                      className="text-red-700"
                    />
                  </>
                )}
              </>
            }
          >
            <ViewModelItemTitle viewModel={viewModel} workspace={workspace} searchKeyword={searchKeyword} />
          </SidebarMenuItem>
        </div>

        {isOpen && children && <div className="ml-5">{children}</div>}

        {openModal === 'ViewModelEdit' && (
          <ViewModelSettingModal
            isOpen={openModal === 'ViewModelEdit'}
            onClose={handleModalClose}
            workspace={workspace}
            viewModel={viewModel}
          />
        )}

        {openModal === 'ViewModelDelete' && (
          <ViewModelDeleteConfirmModal
            viewModel={viewModel}
            isOpen={openModal === 'ViewModelDelete'}
            onClose={handleModalClose}
          />
        )}

        {openModal === 'MoveViewModel' && (
          <MoveViewModelModal isOpen={openModal === 'MoveViewModel'} onClose={handleModalClose} viewModel={viewModel} />
        )}

        {currentUserId && openModal === 'MoveToOtherWorkspace' && (
          <MoveToOtherWorkspaceModal
            isOpen={openModal === 'MoveToOtherWorkspace'}
            onClose={handleModalClose}
            viewModel={viewModel}
            userId={currentUserId}
            groupId={workspace.groupId}
            workspaceId={workspace.id}
          />
        )}
      </div>
    );
  }
);

ViewModelItem.displayName = 'ViewModelItem';
